import { Navigate, useParams } from 'react-router-dom';

import { authorized, authorizedLoader, isAdminUser } from '../../../utils/loaders.ts';
import url from '../../../utils/url.ts';
import {
    advancedTabLoader,
    eventDetailLoader,
    eventListLoader,
    generalTabLoader,
    organizationsTabLoader,
} from './loaders.ts';
import {
    ADMIN_EVENT_ADD_PATH,
    ADMIN_EVENT_ADVANCED_PATH,
    ADMIN_EVENT_ANALYTICS_PATH,
    ADMIN_EVENT_ATTENDEES_PATH,
    ADMIN_EVENT_DETAIL_PATH,
    ADMIN_EVENT_GENERAL_PATH,
    ADMIN_EVENT_LIST_PATH,
    ADMIN_EVENT_ORGANIZATIONS_PATH,
    ADMIN_EVENT_PAGES_ADD_PATH,
    ADMIN_EVENT_PAGES_EDIT_PATH,
    ADMIN_EVENT_PAGES_PATH,
    ADMIN_EVENT_SESSIONS_ADD_PATH,
    ADMIN_EVENT_SESSIONS_EDIT_PATH,
    ADMIN_EVENT_SESSIONS_PATH,
    ADMIN_SCHEDULED_EMAILS_ADD_PATH,
    ADMIN_SCHEDULED_EMAILS_DETAIL_PATH,
    ADMIN_SCHEDULED_EMAILS_PATH,
} from './paths.ts';

const DetailRedirect = () => {
    const { eventId } = useParams();
    return <Navigate to={url(ADMIN_EVENT_GENERAL_PATH, { eventId })} replace={true} />;
};

export default [
    {
        handle: {
            title: ['admin:eventListPage.documentTitle'],
        },
        path: ADMIN_EVENT_LIST_PATH,
        lazy: async () => ({ Component: (await import('./pages/EventListPage.tsx')).default }),
        loader: authorizedLoader(eventListLoader, isAdminUser),
        children: [
            {
                handle: {
                    title: ['admin:eventListPage.formDialog.titleCreate'],
                },
                path: ADMIN_EVENT_ADD_PATH,
                lazy: async () => ({ Component: (await import('./pages/EventListPage.tsx')).AddEventFormDialog }),
                loader: authorized(isAdminUser),
            },
        ],
    },
    {
        handle: {
            title: ['admin:eventDetailPage.documentTitle'],
        },
        path: ADMIN_EVENT_DETAIL_PATH,
        lazy: async () => ({ Component: (await import('./pages/EventDetailPage.tsx')).default }),
        loader: authorizedLoader(eventDetailLoader, isAdminUser),
        children: [
            {
                handle: {
                    title: ['admin:eventDetailPage.generalTab.documentTitle'],
                },
                index: true,
                element: <DetailRedirect />,
            },
            {
                handle: {
                    title: ['admin:eventDetailPage.generalTab.documentTitle'],
                },
                path: ADMIN_EVENT_GENERAL_PATH,
                lazy: async () => ({ Component: (await import('./tabs/GeneralTab.tsx')).default }),
                loader: authorizedLoader(generalTabLoader, isAdminUser),
            },
            {
                handle: {
                    title: ['admin:eventDetailPage.advancedTab.documentTitle'],
                },
                path: ADMIN_EVENT_ADVANCED_PATH,
                lazy: async () => ({ Component: (await import('./tabs/AdvancedTab.tsx')).default }),
                loader: authorizedLoader(advancedTabLoader, isAdminUser),
            },
            {
                handle: {
                    title: ['admin:eventDetailPage.eventSessionsTab.documentTitle'],
                },
                path: ADMIN_EVENT_SESSIONS_PATH,
                lazy: async () => ({ Component: (await import('./tabs/EventSessionsTab.tsx')).default }),
                loader: authorized(isAdminUser),
                children: [
                    {
                        handle: {
                            title: ['admin:eventDetailPage.eventSessionsTab.addDocumentTitle'],
                        },
                        path: ADMIN_EVENT_SESSIONS_ADD_PATH,
                        lazy: async () => ({
                            Component: (await import('./tabs/EventSessionsTab.tsx')).AddEventSessionFormDialog,
                        }),
                        loader: authorized(isAdminUser),
                    },
                    {
                        handle: {
                            title: ['admin:eventDetailPage.eventSessionsTab.editDocumentTitle'],
                        },
                        path: ADMIN_EVENT_SESSIONS_EDIT_PATH,
                        lazy: async () => ({
                            Component: (await import('./tabs/EventSessionsTab.tsx')).EditEventSessionFormDialog,
                        }),
                        loader: authorized(isAdminUser),
                    },
                ],
            },
            {
                handle: {
                    title: ['admin:eventDetailPage.organizationsTab.documentTitle'],
                },
                path: ADMIN_EVENT_ORGANIZATIONS_PATH,
                lazy: async () => ({ Component: (await import('./tabs/OrganizationsTab.tsx')).default }),
                loader: authorizedLoader(organizationsTabLoader, isAdminUser),
            },
            {
                handle: {
                    title: ['admin:eventDetailPage.attendeesTab.documentTitle'],
                },
                path: ADMIN_EVENT_ATTENDEES_PATH,
                lazy: async () => ({ Component: (await import('./tabs/AttendeesTab.tsx')).default }),
                loader: authorized(isAdminUser),
            },

            {
                handle: {
                    title: ['admin:eventDetailPage.eventPagesTab.documentTitle'],
                },
                path: ADMIN_EVENT_PAGES_PATH,
                lazy: async () => ({ Component: (await import('./tabs/PagesTab.tsx')).default }),
                loader: authorized(isAdminUser),
                children: [
                    {
                        handle: {
                            title: ['admin:eventDetailPage.eventPagesTab.addDocumentTitle'],
                        },
                        path: ADMIN_EVENT_PAGES_ADD_PATH,
                        lazy: async () => ({ Component: (await import('./tabs/PagesTab.tsx')).AddEventPageFormDialog }),
                        loader: authorized(isAdminUser),
                    },
                    {
                        handle: {
                            title: ['admin:eventDetailPage.eventPagesTab.editDocumentTitle'],
                        },
                        path: ADMIN_EVENT_PAGES_EDIT_PATH,
                        lazy: async () => ({
                            Component: (await import('./tabs/PagesTab.tsx')).EditEventPageFormDialog,
                        }),
                        loader: authorized(isAdminUser),
                    },
                ],
            },
            {
                handle: {
                    title: ['admin:eventDetailPage.analyticsTab.documentTitle'],
                },
                path: ADMIN_EVENT_ANALYTICS_PATH,
                lazy: async () => ({ Component: (await import('./tabs/AnalyticsTab.tsx')).default }),
                loader: authorized(isAdminUser),
            },
            {
                handle: {
                    title: ['admin:eventDetailPage.scheduledEmailsTab.documentTitle'],
                    hideNavigation: true,
                    hideFooter: true,
                },
                path: ADMIN_SCHEDULED_EMAILS_PATH,
                lazy: async () => {
                    const Component = (await import('./tabs/ScheduledEmailsTab.tsx')).default;
                    return { Component: () => <Component add /> };
                },
                children: [
                    {
                        handle: {
                            title: ['admin:eventDetailPage.scheduledEmailsTab.documentTitle'],
                            hideNavigation: true,
                            hideFooter: true,
                        },
                        path: ADMIN_SCHEDULED_EMAILS_ADD_PATH,
                        lazy: async () => {
                            const Component = (await import('./tabs/ScheduledEmailsTab.tsx')).default;
                            return { Component: () => <Component add /> };
                        },
                    },
                    {
                        handle: {
                            title: ['admin:eventDetailPage.scheduledEmailsTab.documentTitle'],
                            hideNavigation: true,
                            hideFooter: true,
                        },
                        path: ADMIN_SCHEDULED_EMAILS_DETAIL_PATH,
                        lazy: async () => ({ Component: (await import('./tabs/ScheduledEmailsTab.tsx')).default }),
                    },
                ],
            },
        ],
    },
];
